/**
 * Stockaily Analytics Adapter — PostHog EU, consent-first, native
 * `$pageview` / `$pageleave` Web Analytics.
 *
 * Design guarantees:
 *   * Zero network / storage / SDK load before Analytics consent.
 *   * SDK imported lazily via `import("posthog-js")`.
 *   * ONLY allowlisted event names and property keys are emitted.
 *   * A sanitizer strips every full URL, query string, ticker,
 *     portfolio, watchlist, note, label, PII or financial value a
 *     caller might accidentally include.
 *   * Duplicate initialization impossible (StrictMode-safe); duplicate
 *     $pageview impossible (initial / route-change / consent
 *     transition).
 *   * Missing env config → fails closed (no init, no error thrown,
 *     dev-only `console.warn`).
 *   * PostHog EU host only. US host rejected.
 *   * Withdrawal → immediate opt-out + reset + clear PostHog storage.
 *
 * Native Web Analytics lifecycle (manual, no autocapture):
 *   * `capture_pageview` / `capture_pageleave` stay OFF so the
 *     application remains the only source of these events.
 *   * One initial `$pageview` is emitted from inside `posthog.loaded`
 *     (after consent + SDK ready + anonymous `distinct_id` ready).
 *   * SPA pathname changes emit exactly one `$pageleave` for the
 *     previous canonical page, then one `$pageview` for the new one.
 *   * Query-only and fragment-only changes emit nothing.
 *   * A best-effort `$pageleave` fires on `pagehide` for native
 *     bounce-rate / session-duration calculation.
 *   * Only a privacy-safe canonical `$pathname` from a bounded set is
 *     ever sent — never the raw path.
 */
import consent, { registerConsentService, subscribe as subscribeConsent } from "@/lib/consent";
import {
  ALLOWED_EVENTS,
  ALLOWED_PROPERTIES,
  ALLOWED_CANONICAL_PATHNAMES,
  ROUTE_TO_PAGE_GROUP,
  POSTHOG_LOCALSTORAGE_KEY_PREFIX,
} from "@/lib/analytics-allowlist";
import { resolveAppEnvironment } from "@/lib/appEnvironment";
import { isOwnerExcluded } from "@/lib/ownerExclusion";

const EU_HOST_ALLOWED = /^https:\/\/eu\.i\.posthog\.com\/?$/;

function _ownerExclusionActive() {
  try { return isOwnerExcluded(); } catch { return false; }
}

let _posthog = null;
let _initialized = false;
let _initPromise = null;

// Native-Web-Analytics state.
let _lastPageview = null;         // last canonical $pathname sent
let _initialPageviewSent = false; // guards against double initial fire
let _attributionCaptured = false; // first-eligible UTM/referrer attach
let _unloadBound = false;         // one-shot pagehide binding

/**
 * Resolve PostHog config.  Runtime-config.js (served by nginx before
 * the SPA bundle) wins over CRA build-time env.
 */
function _readEnv() {
  let token = "";
  let host = "";
  try {
    if (
      typeof window !== "undefined" &&
      window.__STOCKAILY_RUNTIME_CONFIG__ &&
      typeof window.__STOCKAILY_RUNTIME_CONFIG__ === "object"
    ) {
      const rc = window.__STOCKAILY_RUNTIME_CONFIG__;
      if (typeof rc.posthogKey === "string" && rc.posthogKey.trim() !== "") {
        token = rc.posthogKey.trim();
      }
      if (typeof rc.posthogHost === "string" && rc.posthogHost.trim() !== "") {
        host = rc.posthogHost.trim();
      }
    }
  } catch (_e) {
    /* fall through */
  }
  if (!token) token = process.env.REACT_APP_POSTHOG_KEY || "";
  if (!host)  host  = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
  // Delegate to the centralised, validated resolver.  Never trust
  // NODE_ENV=production on its own as evidence that this is the
  // Stockaily production deployment; the resolver below honors a
  // 5-step precedence (runtime-config → REACT_APP_ENV → strict
  // hostname mapping → test → "development").
  const env = resolveAppEnvironment();
  return { token, host, env };
}

export function isConfigured() {
  const { token, host } = _readEnv();
  if (!token || typeof token !== "string" || token.length < 8) return false;
  if (!EU_HOST_ALLOWED.test(host)) return false;
  return true;
}

export function isActive() {
  return _initialized && consent.getCategory("analytics") === true;
}

/* ---------------------- Canonical-pathname mapping ---------------- */

/**
 * Map an arbitrary SPA pathname to the bounded canonical pathname sent
 * to PostHog.  Query string, fragment, ticker, user id, reset token,
 * portfolio id, free text etc. are stripped by never looking at them.
 * Only the pathname is inspected and only a known-safe short string
 * from `ALLOWED_CANONICAL_PATHNAMES` is returned.
 */
export function canonicalPathname(pathname) {
  if (!pathname || typeof pathname !== "string") return "/other";
  for (const [prefix, canonical] of ROUTE_TO_PAGE_GROUP) {
    if (
      pathname === prefix ||
      pathname.startsWith(prefix + "/") ||
      pathname.startsWith(prefix + "?")
    ) {
      // The second element of each ROUTE_TO_PAGE_GROUP row is now the
      // canonical pathname (e.g. "/pricing"); the explicit allowlist
      // below is a final hard gate so a mistaken map entry can never
      // leak an unexpected value to PostHog.
      if (ALLOWED_CANONICAL_PATHNAMES.has(canonical)) return canonical;
      return "/other";
    }
  }
  return "/other";
}

/** Coarse page_group used by legacy funnel events (not $pageview). */
export function pageGroupForPath(pathname) {
  // Translate canonical pathname → page_group token for continuity
  // with existing funnel events.  "/portfolio" → "portfolio", etc.
  const cp = canonicalPathname(pathname);
  if (cp === "/") return "landing";
  if (cp === "/other") return "other";
  return cp.replace(/^\//, "");
}

/* -------------------- UTM / referrer sanitization ---------------- */

const _UTM_SAFE_RE = /^[A-Za-z0-9._-]{1,64}$/;

function _safeUtmValue(raw) {
  if (typeof raw !== "string") return null;
  // Decode safely — malformed % escapes throw.
  let decoded;
  try {
    decoded = decodeURIComponent(raw);
  } catch (_e) {
    return null;
  }
  if (decoded.length === 0 || decoded.length > 64) return null;
  // Strict safe set: letters, numbers, dot, underscore, hyphen.
  if (!_UTM_SAFE_RE.test(decoded)) return null;
  // Normalize to lowercase.
  return decoded.toLowerCase();
}

function _safeReferringDomain(referrerUrl, selfOrigin) {
  if (typeof referrerUrl !== "string" || !referrerUrl) return null;
  try {
    const u = new URL(referrerUrl);
    const host = u.hostname.toLowerCase();
    if (!host || host.length > 253) return null;
    // Reject anything that doesn't look like a bare hostname.
    if (!/^[a-z0-9.-]+$/.test(host)) return null;
    // First-party self-referral is useless and privacy-noisy.
    if (selfOrigin && host === selfOrigin.toLowerCase()) return null;
    return host;
  } catch (_e) {
    return null;
  }
}

/**
 * Read sanitized attribution from the CURRENT url + document.referrer.
 * Only called AFTER consent, and only ONCE per browser session for
 * the first eligible $pageview.
 */
function _readAttribution() {
  const out = {};
  if (typeof window === "undefined") return out;
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const src = _safeUtmValue(sp.get("utm_source"));
    const med = _safeUtmValue(sp.get("utm_medium"));
    const cmp = _safeUtmValue(sp.get("utm_campaign"));
    if (src) out.utm_source = src;
    if (med) out.utm_medium = med;
    if (cmp) out.utm_campaign = cmp;
  } catch (_e) { /* ignore */ }
  try {
    const refUrl = (typeof document !== "undefined" && document.referrer) || "";
    const selfOrigin = window.location && window.location.hostname;
    const dom = _safeReferringDomain(refUrl, selfOrigin);
    if (dom) out.referring_domain = dom;
  } catch (_e) { /* ignore */ }
  return out;
}

/* -------------------------- init ---------------------------------- */

export async function initPostHog() {
  if (_initialized) return _posthog;
  if (_initPromise) return _initPromise;
  if (consent.getCategory("analytics") !== true) return null;
  if (_ownerExclusionActive()) return null;
  if (!isConfigured()) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "[analytics] REACT_APP_POSTHOG_KEY missing or REACT_APP_POSTHOG_HOST is not the EU host. PostHog remains inactive.",
      );
    }
    return null;
  }
  const { token, host, env } = _readEnv();
  _initPromise = import("posthog-js").then((mod) => {
    const posthog = mod.default || mod;
    posthog.init(token, {
      api_host: host,
      ui_host: "https://eu.posthog.com",

      /* ------- Every invasive subsystem explicitly disabled ------- */
      autocapture: false,
      capture_pageview: false,        // native pageviews are MANUAL
      capture_pageleave: false,       // native pageleaves are MANUAL
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_performance: false,
      capture_exceptions: false,
      disable_session_recording: true,
      disable_surveys: true,
      advanced_disable_feature_flags: true,
      advanced_disable_feature_flags_on_first_load: true,

      /* ------- Capture is strictly consent-gated ------------------ */
      opt_out_capturing_by_default: true,
      opt_out_persistence_by_default: true,
      persistence: "localStorage",
      cross_subdomain_cookie: false,

      /* ------- Default production bot filtering RESTORED ---------- *
       * PostHog's built-in UA filter drops Playwright/HeadlessChrome
       * so the production launch funnel is not polluted by bot
       * traffic.  Non-production environments keep the filter off so
       * Playwright can verify the ingest path.
       * ------------------------------------------------------------ */
      opt_out_useragent_filter: env !== "production",

      request_batching: false,
      debug: false,
      before_send: _beforeSend(env),

      /**
       * `loaded` fires after the SDK is ready AND an anonymous
       * distinct_id exists in persistence.  We opt in to capturing
       * here and emit the initial `$pageview` from inside the same
       * callback — this is the only point where consent + SDK + id
       * are all simultaneously true.
       */
      loaded: (ph) => {
        try { ph.opt_in_capturing(); } catch (_) {}
        _bindPageHideOnce(ph);
        _emitInitialPageview(ph);
      },
    });
    _posthog = posthog;
    _initialized = true;
    return posthog;
  }).catch((e) => {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[analytics] posthog-js failed to load:", e && e.message);
    }
    _initPromise = null;
    return null;
  });
  return _initPromise;
}

/* --------- Native $pageview / $pageleave emission ----------------- */

function _getDistinctId(ph) {
  try {
    const d = typeof ph.get_distinct_id === "function" ? ph.get_distinct_id() : null;
    if (typeof d === "string" && d.length >= 4) return d;
  } catch (_e) { /* ignore */ }
  return null;
}

function _getLocale() {
  try {
    const el = typeof document !== "undefined" ? document.documentElement : null;
    const lang = el && el.lang ? String(el.lang).toLowerCase() : "";
    if (lang.startsWith("de")) return "de";
    if (lang.startsWith("en")) return "en";
  } catch (_e) { /* ignore */ }
  return null;
}

function _buildPageProps(canonical, { withAttribution } = {}) {
  const props = {
    $pathname: canonical,
    page_group: pageGroupForPath(
      typeof window !== "undefined" && window.location
        ? window.location.pathname
        : "/",
    ),
  };
  const locale = _getLocale();
  if (locale) props.locale = locale;
  if (withAttribution) {
    const attr = _readAttribution();
    if (attr.utm_source)       props.utm_source = attr.utm_source;
    if (attr.utm_medium)       props.utm_medium = attr.utm_medium;
    if (attr.utm_campaign)     props.utm_campaign = attr.utm_campaign;
    if (attr.referring_domain) props.referring_domain = attr.referring_domain;
  }
  return props;
}

function _emitInitialPageview(ph) {
  if (_initialPageviewSent) return;
  if (consent.getCategory("analytics") !== true) return;
  if (_ownerExclusionActive()) return;
  if (!_getDistinctId(ph)) return;
  const pathname =
    (typeof window !== "undefined" && window.location && window.location.pathname) || "/";
  const canonical = canonicalPathname(pathname);
  const props = _buildPageProps(canonical, { withAttribution: !_attributionCaptured });
  try {
    ph.capture("$pageview", props);
    _lastPageview = canonical;
    _initialPageviewSent = true;
    if ("utm_source" in props || "utm_medium" in props || "utm_campaign" in props || "referring_domain" in props) {
      _attributionCaptured = true;
    } else {
      // Even when no UTMs/referrer are present, we only read the URL
      // ONCE per session.  Mark attribution captured so we never read
      // it again on later route changes.
      _attributionCaptured = true;
    }
  } catch (_e) { /* ignore */ }
}

/**
 * React Router SPA pathname change notifier.  Called by
 * `RouteChangeObserver` after a canonical pathname transition.
 */
export function trackSpaRouteChange(toPathname) {
  if (!_initialized || !_posthog) return;
  if (consent.getCategory("analytics") !== true) return;
  if (_ownerExclusionActive()) return;
  if (!_getDistinctId(_posthog)) return;
  const canonical = canonicalPathname(toPathname);
  if (canonical === _lastPageview) return;
  // Emit $pageleave for the previous canonical, then $pageview for
  // the new canonical.  Attribution is NOT re-attached on route
  // changes.
  try {
    if (_lastPageview) {
      _posthog.capture("$pageleave", { $pathname: _lastPageview });
    }
  } catch (_e) { /* ignore */ }
  try {
    _posthog.capture("$pageview", _buildPageProps(canonical));
    _lastPageview = canonical;
  } catch (_e) { /* ignore */ }
}

function _bindPageHideOnce(ph) {
  if (_unloadBound) return;
  if (typeof window === "undefined" || typeof window.addEventListener !== "function") return;
  _unloadBound = true;
  const handler = () => {
    if (!_initialized || !_posthog) return;
    if (consent.getCategory("analytics") !== true) return;
    if (_ownerExclusionActive()) return;
    if (!_lastPageview) return;
    try {
      ph.capture("$pageleave", { $pathname: _lastPageview });
    } catch (_e) { /* ignore */ }
    // Prevent the SPA path from firing another $pageleave for the
    // same path if the handler runs more than once.
    _lastPageview = null;
  };
  try { window.addEventListener("pagehide", handler); } catch (_e) {}
}

/* -------------------- before_send sanitizer ----------------------- */

function _beforeSend(appEnvironment) {
  const ALLOWED_AUTO_PROPS = new Set([
    "$lib", "$lib_version", "$insert_id",
    "$device_id", "$session_id", "$window_id", "$anon_distinct_id",
    "$pathname", // we send this ourselves — allowlist it explicitly
  ]);
  return function (event) {
    if (!event || typeof event !== "object") return null;
    const name = event.event;
    if (!ALLOWED_EVENTS.has(name)) return null;
    const src = event.properties || {};
    const cleaned = {};
    for (const key of Object.keys(src)) {
      if (!ALLOWED_PROPERTIES.has(key) && !ALLOWED_AUTO_PROPS.has(key)) continue;
      const v = src[key];
      if (typeof v === "string") {
        if (v.length > 200) continue;
        if (/^https?:\/\//i.test(v)) continue;
        // $pathname is permitted to contain exactly one leading "/" —
        // other string props must not contain "?" or "#".
        if (key !== "$pathname" && (v.includes("?") || v.includes("#"))) continue;
      }
      cleaned[key] = v;
    }
    cleaned.app_environment = appEnvironment;
    if (typeof src.token === "string" && src.token) cleaned.token = src.token;
    if (typeof src.distinct_id === "string" && src.distinct_id) {
      cleaned.distinct_id = src.distinct_id;
    }
    event.properties = cleaned;
    return event;
  };
}

/* --------------- Public funnel-event capture ---------------------- */

export function capture(eventName, props) {
  if (!_initialized || !_posthog) return;
  if (consent.getCategory("analytics") !== true) return;
  if (_ownerExclusionActive()) return;
  if (!ALLOWED_EVENTS.has(eventName)) return;
  const clean = {};
  const src = props && typeof props === "object" ? props : {};
  for (const k of Object.keys(src)) {
    if (ALLOWED_PROPERTIES.has(k)) clean[k] = src[k];
  }
  try { _posthog.capture(eventName, clean); } catch (_) {}
}

/* ----------------------- Withdrawal ------------------------------- */

export function withdraw() {
  if (_posthog) {
    try { _posthog.opt_out_capturing(); } catch (_) {}
    try { _posthog.reset(); } catch (_) {}
  }
  _initialized = false;
  _posthog = null;
  _initPromise = null;
  _lastPageview = null;
  _initialPageviewSent = false;
  _attributionCaptured = false;
  _unloadBound = false;
  try {
    if (typeof window !== "undefined") {
      const keys = Object.keys(window.localStorage);
      for (const k of keys) {
        if (k.startsWith(POSTHOG_LOCALSTORAGE_KEY_PREFIX)) {
          window.localStorage.removeItem(k);
        }
      }
    }
  } catch (_) {}
}

/* ----------------------- Consent plumbing ------------------------- */

let _registered = false;
export function registerAnalyticsWithConsent() {
  if (_registered) return;
  _registered = true;
  registerConsentService({
    id: "posthog",
    category: "analytics",
    onActivate: () => { initPostHog(); },
    onDeactivate: () => { withdraw(); },
    cleanupKeys: [],
  });
  subscribeConsent(() => {
    if (consent.getCategory("analytics") === true) initPostHog();
  });
}

/* ----------------------- Test-only helpers ----------------------- */

export function __reset() {
  _initialized = false;
  _posthog = null;
  _initPromise = null;
  _registered = false;
  _lastPageview = null;
  _initialPageviewSent = false;
  _attributionCaptured = false;
  _unloadBound = false;
}
export function __setPosthogForTests(fake) {
  _posthog = fake;
  _initialized = !!fake;
}
export function __beforeSendForTests(env) { return _beforeSend(env); }
export function _testGetPosthog() { return _posthog; }

export default {
  initPostHog,
  capture,
  withdraw,
  isActive,
  isConfigured,
  trackSpaRouteChange,
  canonicalPathname,
  pageGroupForPath,
  registerAnalyticsWithConsent,
};
